import { ReactElement, useEffect, useId, useState } from 'react';
import {
  allowAll,
  defaultDenied,
  OPEN_CONSENT_EVENT,
  readConsent,
  saveConsent,
  type ConsentStateInterface,
} from '../consent';

interface CookieItemInterface {
  name: string;
  provider: string;
  description: string;
  duration: string;
  type: string;
}

interface CategoryInterface {
  id: 'marketing' | 'necessary' | 'preferences' | 'statistics';
  title: string;
  intro: string;
  locked?: boolean;
  empty?: string;
  cookies: CookieItemInterface[];
}

const CATEGORIES: CategoryInterface[] = [
  {
    id: 'necessary',
    title: 'Necessary',
    locked: true,
    intro:
      'Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.',
    cookies: [
      {
        name: 'sc_cookie_consent',
        provider: 'starkcrawl.com',
        description:
          "Stores the user's cookie consent state for the current domain.",
        duration: '1 year',
        type: 'HTTP Cookie',
      },
      {
        name: 'test_cookie',
        provider: 'Google',
        description: "Used to check if the user's browser supports cookies.",
        duration: '1 day',
        type: 'HTTP Cookie',
      },
      {
        name: 'tsr-scroll-restoration-v1_3',
        provider: 'starkcrawl.com',
        description:
          'Remembers scroll position when navigating between pages.',
        duration: 'Session',
        type: 'HTML Local Storage',
      },
    ],
  },
  {
    id: 'preferences',
    title: 'Preferences',
    intro:
      'Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in.',
    empty: 'We do not use cookies of this type.',
    cookies: [],
  },
  {
    id: 'statistics',
    title: 'Statistics',
    intro:
      'Statistic cookies help website owners to understand how visitors interact with websites by collecting and reporting information anonymously.',
    cookies: [
      {
        name: 'mp_*_mixpanel',
        provider: 'Mixpanel',
        description:
          'Stores a unique identifier used to recognise the visitor across visits for product analytics.',
        duration: '1 year',
        type: 'HTTP Cookie',
      },
      {
        name: '_ga',
        provider: 'Google',
        description:
          'Registers a unique ID that is used to generate statistical data on how the visitor uses the website.',
        duration: '2 years',
        type: 'HTTP Cookie',
      },
      {
        name: '_ga_#',
        provider: 'Google',
        description:
          'Used by Google Analytics to persist session state.',
        duration: '2 years',
        type: 'HTTP Cookie',
      },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing',
    intro:
      'Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.',
    cookies: [
      {
        name: '__sc_gclid',
        provider: 'starkcrawl.com',
        description:
          'Stores the Google Ads click id so a later sign-up can be attributed to the ad.',
        duration: '90 days',
        type: 'HTTP Cookie',
      },
      {
        name: '_gcl_au',
        provider: 'Google',
        description:
          'Used by Google AdSense to experiment with advertisement efficiency across websites.',
        duration: '3 months',
        type: 'HTTP Cookie',
      },
      {
        name: '_gcl_ls',
        provider: 'Google',
        description: 'Used by Google to store and track conversions.',
        duration: 'Persistent',
        type: 'HTML Local Storage',
      },
      {
        name: 'IDE',
        provider: 'Google',
        description:
          "Used by Google DoubleClick to register and report the website user's actions after viewing or clicking one of the advertiser's ads.",
        duration: '1 year',
        type: 'HTTP Cookie',
      },
      {
        name: 'pagead/1p-user-list/#',
        provider: 'Google',
        description:
          'Tracks if the user has shown interest in specific products or events across multiple websites.',
        duration: 'Session',
        type: 'Pixel Tracker',
      },
    ],
  },
];

const buttonClass = (variant: 'primary' | 'secondary'): string =>
  variant === 'primary'
    ? 'w-full cursor-pointer rounded-[3.75px] border-2 border-primary-400 bg-primary-400 px-4 py-[15px] text-center text-[15px] font-semibold leading-6 text-white'
    : 'mt-[7.5px] w-full cursor-pointer rounded-[3.75px] border-2 border-white bg-[#232323] px-4 py-[15px] text-center text-[15px] font-semibold leading-6 text-white';

export const CookieConsent = (): ReactElement | null => {
  const titleId = useId();
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [consent, setConsent] = useState<ConsentStateInterface>(defaultDenied);

  useEffect(() => {
    const saved = readConsent();

    if (saved) setConsent(saved);

    setOpen(!saved);

    setReady(true);

    const reopen = (): void => {
      setConsent(readConsent() ?? defaultDenied());

      setDetails(true);

      setOpen(true);
    };

    window.addEventListener(OPEN_CONSENT_EVENT, reopen);

    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  if (!ready || !open) return null;

  const choose = (next: ConsentStateInterface): void => {
    saveConsent(next);

    setConsent(next);

    setOpen(false);

    setDetails(false);
  };

  return (
    <div
      id="cookie-consent"
      role="region"
      aria-labelledby={titleId}
      className="fixed bottom-4 left-4 z-90 w-[min(21.5rem,calc(100vw-2rem))] rounded-lg bg-background-secondary font-open text-[15px] leading-6 text-white shadow-[0_32px_68px_rgba(0,0,0,0.3)]"
    >
      <div className="px-[9.375px] py-[24.375px]">
        <div id={titleId} className="mb-[7.5px] font-semibold">
          This website uses cookies
        </div>
        <p>
          We use cookies to personalise content, measure our advertising and
          analyse traffic. Some data is shared with our partners.
        </p>
        <button
          type="button"
          className="mt-3 cursor-pointer font-semibold text-primary-500"
          onClick={() => setDetails((value) => !value)}
        >
          {details ? 'Hide details' : 'Show details'}
        </button>
      </div>

      {details && (
        <div className="max-h-[50vh] overflow-y-auto border-t border-white/15 px-[9.375px] py-4">
          {CATEGORIES.map((category) => {
            const checked = consent[category.id];
            const isOpen = expanded === category.id;

            return (
              <div key={category.id} className="border-b border-white/10 py-3">
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    className="cursor-pointer text-left font-semibold"
                    aria-expanded={isOpen}
                    onClick={() => setExpanded(isOpen ? null : category.id)}
                  >
                    {category.title}
                    <span className="ml-2 font-normal text-grey-300">
                      {category.cookies.length}
                    </span>
                  </button>
                  <input
                    type="checkbox"
                    className="size-4 accent-primary-400"
                    checked={checked}
                    disabled={category.locked}
                    aria-label={category.title}
                    onChange={(event) =>
                      setConsent((current) => ({
                        ...current,
                        [category.id]: event.target.checked,
                      }))
                    }
                  />
                </div>
                {isOpen && (
                  <div className="mt-2 space-y-3 text-sm text-grey-200">
                    <p>{category.intro}</p>
                    {category.cookies.length === 0 && category.empty && (
                      <p>{category.empty}</p>
                    )}
                    {category.cookies.map((cookie) => (
                      <div key={cookie.name}>
                        <div className="font-semibold text-white">
                          {cookie.provider}
                        </div>
                        <div className="text-white">{cookie.name}</div>
                        <p>{cookie.description}</p>
                        <p className="text-xs text-grey-300">
                          Maximum storage duration: {cookie.duration}. Type:{' '}
                          {cookie.type}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="px-[15px] pb-[15px]">
        <button
          type="button"
          className={buttonClass('primary')}
          onClick={() => choose(allowAll())}
        >
          Allow all
        </button>
        {details && (
          <button
            type="button"
            className={buttonClass('secondary')}
            onClick={() => choose(consent)}
          >
            Allow selection
          </button>
        )}
        <button
          type="button"
          className={buttonClass('secondary')}
          onClick={() => choose(defaultDenied())}
        >
          Deny
        </button>
      </div>
    </div>
  );
};
